import knowhowMark from '../assets/knowhow-mark.svg';

/** Default site agent for KnowHow Labs — Noel KnowHow AIA Copilot. Icon falls back until API data loads. */
export const DEFAULT_AGENT = {
  label: 'Noel KnowHow AIA Copilot',
  value: 'b4f14408-928b-47b1-b9fd-fd3a548a0200',
  icon: knowhowMark,
  mode: 'agent',
};

export const DEFAULT_AGENT_ID = DEFAULT_AGENT.value;
export const DEFAULT_AGENT_NAME = DEFAULT_AGENT.label;
export const DEFAULT_AGENT_ICON = knowhowMark;

/** Legacy Albert ids still present in older localStorage / UI. */
const LEGACY_DEFAULT_IDS = new Set([
  'b4f14408-928b-47b1-b9fd-fd3a548a0200',
  '6d1cd244-2e8e-4782-981d-24cae676f7c7',
  'ferdy',
]);

function normalizeAgentName(agent) {
  return (agent?.label || agent?.agent_name || '').toLowerCase().trim();
}

export function isNoelAgentName(name) {
  const n = (name || '').toLowerCase().trim();
  if (!n) return false;
  if (n === 'noel knowhow aia copilot') return true;
  if (n === 'noel') return true;
  if (n.includes('noel') && (n.includes('copilot') || n.includes('knowhow') || n.includes('aia'))) return true;
  return false;
}

export function isDefaultAgent(agent) {
  if (!agent) return false;
  const id = String(agent.value ?? agent.id ?? agent.agent_id ?? '').trim();
  const name = normalizeAgentName(agent);
  return (
    id === DEFAULT_AGENT_ID ||
    LEGACY_DEFAULT_IDS.has(id) ||
    isNoelAgentName(name) ||
    name === 'albert' ||
    name === 'albert chat' ||
    name === 'ferdy'
  );
}

export function findDefaultAgent(agents) {
  if (!agents?.length) return null;

  const byNoel = agents.find(a => isNoelAgentName(a.agent_name || a.label));
  if (byNoel) return byNoel;

  return (
    agents.find(a => String(a.id ?? a.agent_id) === DEFAULT_AGENT_ID) ||
    agents.find(a => {
      const name = (a.agent_name || a.label || '').toLowerCase().trim();
      return name === 'albert' || name === 'albert chat';
    }) ||
    null
  );
}

/** Map an API agent to a select/context option; keep API icon when present. */
export function toDefaultAwareAgentOption(agent) {
  if (!agent) return null;
  const value = agent.id ?? agent.agent_id ?? agent.value;
  const icon = agent.icon ?? null;
  if (isDefaultAgent({ ...agent, value })) {
    return {
      ...DEFAULT_AGENT,
      // Prefer the live Noel name from API when present; otherwise the Noel default label.
      label: isNoelAgentName(agent.agent_name || agent.label)
        ? agent.agent_name || agent.label
        : DEFAULT_AGENT.label,
      value: value || DEFAULT_AGENT.value,
      icon: icon || DEFAULT_AGENT.icon,
    };
  }
  return {
    label: agent.agent_name || agent.label,
    value,
    icon,
    mode: agent.mode || 'agent',
  };
}

/** Normalize persisted agent selection; preserve API icons. */
export function normalizeStoredAgent(agent) {
  if (!agent) return { ...DEFAULT_AGENT };
  const icon = agent.icon ?? null;
  if (isDefaultAgent(agent)) {
    const name = agent.label || agent.agent_name;
    return {
      ...DEFAULT_AGENT,
      label: isNoelAgentName(name) ? name : DEFAULT_AGENT.label,
      value: agent.value ?? agent.id ?? agent.agent_id ?? DEFAULT_AGENT.value,
      icon: icon || DEFAULT_AGENT.icon,
    };
  }
  return {
    label: agent.label || agent.agent_name || 'Agent',
    value: agent.value ?? agent.id ?? agent.agent_id,
    icon,
    mode: agent.mode || 'agent',
  };
}
